import { memo, useCallback, useMemo, useState } from 'react';
import {
  Checkbox,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { ViewColumnOutlinedIcon } from '../../assets';
import { useDispatch, updateListParams } from '../../state';
import {
  getColumnDefinitions,
  getColumnVisibilityMap,
} from './columnVisibility';
import { useStyles } from './list.styles';

interface Props {
  resource: string;
  fields?: React.ReactNode;
  columnVisibility?: Record<string, boolean>;
}

export const ColumnVisibilityMenu = memo(
  ({ resource, fields, columnVisibility }: Props) => {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const columnDefinitions = useMemo(
      () => getColumnDefinitions(fields),
      [fields],
    );

    const resolvedColumnVisibility = useMemo(
      () => getColumnVisibilityMap(columnDefinitions, columnVisibility),
      [columnDefinitions, columnVisibility],
    );

    const visibleColumnCount = useMemo(
      () =>
        columnDefinitions.reduce(
          (acc, column) =>
            acc + (resolvedColumnVisibility[column.key] !== false ? 1 : 0),
          0,
        ),
      [columnDefinitions, resolvedColumnVisibility],
    );

    const handleOpen = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
      },
      [],
    );

    const handleClose = useCallback(() => {
      setAnchorEl(null);
    }, []);

    const handleToggle = useCallback(
      (columnKey: string) => {
        if (!resource) {
          return;
        }

        const isCurrentlyVisible =
          resolvedColumnVisibility[columnKey] !== false;
        if (isCurrentlyVisible && visibleColumnCount <= 1) {
          return;
        }
        dispatch(
          updateListParams({
            list: resource,
            params: {
              columnVisibility: {
                ...resolvedColumnVisibility,
                [columnKey]: !resolvedColumnVisibility[columnKey],
              },
            },
          }),
        );
      },
      [dispatch, resource, resolvedColumnVisibility, visibleColumnCount],
    );

    if (!resource) {
      return null;
    }

    return (
      <>
        <Tooltip title='Columns'>
          <span>
            <IconButton
              aria-label='column visibility'
              size='large'
              onClick={handleOpen}
              disabled={!columnDefinitions.length}
            >
              <ViewColumnOutlinedIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { className: classes.columnMenu } }}
        >
          {columnDefinitions.map((column) => {
            const isVisible = resolvedColumnVisibility[column.key] !== false;
            const disableUncheck = isVisible && visibleColumnCount <= 1;
            return (
              <MenuItem
                key={column.key}
                dense
                onClick={() => handleToggle(column.key)}
                disabled={disableUncheck}
              >
                <ListItemIcon>
                  <Checkbox size='small' color='primary' checked={isVisible} />
                </ListItemIcon>
                {column.label}
              </MenuItem>
            );
          })}
        </Menu>
      </>
    );
  },
);
